import { IToken as BeeFreeAuthToken } from '@beefree.io/sdk/dist/types/bee';
import Axios from 'api/client';
import { pickOrUndefined } from 'helpers/pickers';
import qs from 'qs';
import {
    Address,
    AdvancedMessageTemplateDetail,
    AdvancedMessageTemplatesResponse,
    DefaultMergeField,
    EngageConfiguration,
    EngageDelivery,
    EngageDomain,
    EngageMessage,
    EngageMessageChannel,
    EngageMessageEditor,
    EngageMessageMedia,
    EngageMessageStatus,
    EngageMessageTemplate,
    EngageMessageTemplateVisibility,
    EngageSender,
    EngageStatus,
    LetterAggregatedFulfillmentStatus,
    LetterFulfillment,
    MergeField,
    MessageNumber,
    SmsKeyword,
    TransactionSubtype,
} from 'types/domain';
import { PaginatedData } from 'types/pagination';
import { FilterParams } from 'types/params';

interface CreateEngageMessageParams {
    channel: EngageMessageChannel;
    message_template_id?: number;
    table?: EngageMessage['recipient_table'];
    editor?: EngageMessageEditor;
    subject?: string;
    internal_name?: string;
    body?: string;
    body_json?: string | null;
    settings?: Partial<EngageMessage['settings']>;
}

interface FetchEngageMessagesParams extends FilterParams {
    status?: EngageMessageStatus[];
    notStatus?: EngageMessageStatus[];
    friendlyStatus?: EngageMessageStatus[];
    channel?: EngageMessageChannel[];
    ordered?: number;
    printable?: number;
}

interface UpdateEngageMessageNumberParams {
    forwarding_email: string;
    response_message: string;
    forwarding_criteria: string;
}

interface MergeFieldsParams {
    limit?: number;
    page?: number;
    filterBy?: string;
}

interface CreateAccountMessageTemplateParams {
    name: string;
    description?: string;
    channel: EngageMessageChannel;
    subject?: string;
    body: string | null;
    body_json?: string | null;
    editor?: EngageMessageEditor;
}

interface FetchAdvancedEmailTemplateParams {
    page?: number;
    template_type?: string;
    category?: string | string[];
    limit?: number;
    search?: string;
}

interface FetchMessageCheckoutDetailsResponse {
    session_id: string;
    order_number: string | null;
    invoice_url: string | null;
    quantity: number;
    unit_price: string;
    total_amount: string;
    tax_amount: string;
    payment_method: {
        id: string;
        cc_last_four: string;
        method_subtype: TransactionSubtype;
        exp_month: number;
        exp_year: number;
        cc_expire: string;
    };
    billing_address: Address;
    email: string;
    phone: string | null;
    name: string | null;
}

interface StripeCheckoutDetails {
    session_id: string;
    client_secret: string;
    quantity: number;
    total_amount: string;
    unit_price: string;
}

interface EngageMessages extends PaginatedData<EngageMessage> {
    counts: {
        all: number;
        sent: number;
        drafts: number;
        queued: number;
        sending: number;
        preparing: number;
        failed: number;
        ordered: number;
        printable: number;
    };
}

interface FetchLetterFulfillmentStatsResponse {
    stats: {
        delivered: number;
        returned_to_sender: number;
        re_routed: number;
    };
    current_status: LetterAggregatedFulfillmentStatus;
}

interface CreateLetterProofStripeSessionParams {
    recipient_name: string;
    city: string;
    zipcode: string;
    state: string;
    address_1: string;
    address_2?: string;
    country: string;
}

interface LetterProofStripeSessionResponse {
    checkout_details: null | StripeCheckoutDetails;
    mail_piece: LetterFulfillment;
}

interface EngageMessagePreviewStatusResponse {
    status: boolean;
}

interface FetchMessageTemplateParams extends FilterParams {
    channel?: EngageMessageChannel;
    visibility?: EngageMessageTemplateVisibility;
}

interface SmsKeywordParams {
    keyword: string;
    response: string;
    model: {
        id?: string;
        table: string;
    };
}

const getEngageConfiguration = (accountId: number) =>
    Axios.get<EngageConfiguration>(`/accounts/${accountId}/engage-configuration`).then(
        response => response.data,
    );

const getAvailableNumbers = (
    accountId: number,
    {
        search,
    }: {
        search: string;
    },
) => {
    const queryArgs: {
        contains: string;
        code?: string;
        state?: string;
    } = {
        contains: '',
    };

    if (parseInt(search)) {
        queryArgs.contains = search.toString();
        queryArgs.code = queryArgs.contains.slice(0, 3);
    } else {
        queryArgs.state = search.replace(/[^a-zA-Z]/g, '');
        queryArgs.contains = search.replace(/\D/g, '');
    }

    return Axios.get(
        `/accounts/${accountId}/search-available-numbers?${qs.stringify(queryArgs)}`,
    ).then(response => response.data);
};

const postPhoneNumber = (
    accountId: number,
    {
        phoneNumber,
    }: {
        phoneNumber: string;
    },
) => {
    return Axios.post(`/accounts/${accountId}/message_numbers`, { number: phoneNumber }).then(
        response => response,
    );
};

const provisionEngageRegistration = (accountId: number) => {
    return Axios.post(`/accounts/${accountId}/provision-engage-registration`).then(
        response => response,
    );
};

const fetchEngageMessages = (accountId: number, params: FetchEngageMessagesParams = {}) => {
    const { channel, status, friendlyStatus, notStatus, ...rest } = params;

    return Axios.get<EngageMessages>(`/accounts/${accountId}/messages`, {
        params: {
            ...rest,
            status: pickOrUndefined(!!status, concatStatusStrings(status)),
            friendlyStatus: pickOrUndefined(!!friendlyStatus, concatStatusStrings(friendlyStatus)),
            notStatus: pickOrUndefined(!!notStatus, concatStatusStrings(notStatus)),
            channel: pickOrUndefined(!!channel, concatStatusStrings(channel)),
        },
    }).then(response => response.data);
};

const fetchEngageMessageById = (accountId: number, messageId: number) =>
    Axios.get<EngageMessage>(`/accounts/${accountId}/messages/${messageId}`).then(
        response => response.data,
    );

const updateEngageMessageById = (
    accountId: number,
    messageId: number,
    params: Partial<
        Omit<EngageMessage, 'settings' | 'sender'> & {
            settings: Partial<EngageMessage['settings']>;
            sender: Pick<EngageSender, 'id' | 'table'>;
        }
    >,
) =>
    Axios.patch<EngageMessage>(`/accounts/${accountId}/messages/${messageId}`, params).then(
        response => response.data,
    );

const duplicateEngageMessage = (accountId: number, messageId: number) =>
    Axios.post<EngageMessage>(`/accounts/${accountId}/messages/${messageId}/duplicate`).then(
        response => response.data,
    );

const unscheduleEngageMessage = (accountId: number, messageId: number) =>
    Axios.post<EngageMessage>(`/accounts/${accountId}/messages/${messageId}/unqueue`).then(
        response => response.data,
    );

const deleteEngageMessage = (accountId: number, messageId: number) =>
    Axios.delete(`/accounts/${accountId}/messages/${messageId}`).then(response => response.data);

const generateEngageLetterZip = (accountId: number, messageId: number) =>
    Axios.post<EngageMessage>(`/accounts/${accountId}/messages/${messageId}/generate-zip`).then(
        response => response.data,
    );

const createEngageMessage = (
    accountId: number,
    {
        channel,
        message_template_id,
        table = 'contacts',
        editor,
        body_json,
        settings,
    }: CreateEngageMessageParams,
) =>
    Axios.post<EngageMessage>(`/accounts/${accountId}/messages`, {
        channel,
        message_template_id,
        recipient_table: table,
        editor,
        body_json,
        settings,
    }).then(response => response.data);

const uploadEngageMessageMedia = (accountId: number, messageId: number, formData: FormData) =>
    Axios.post<EngageMessageMedia>(
        `/accounts/${accountId}/messages/${messageId}/media`,
        formData,
    ).then(response => response.data);
const deleteEngageMessageMedia = (accountId: number, messageId: number, mediaId: number | string) =>
    Axios.delete(`/accounts/${accountId}/messages/${messageId}/media/${mediaId}`).then(
        response => response.data,
    );

const updateEngageMessageNumber = (
    accountId: number,
    phoneNumberId: number,
    params: UpdateEngageMessageNumberParams,
) =>
    Axios.patch(`/accounts/${accountId}/message_numbers/${phoneNumberId}`, params).then(
        response => response,
    );

const deleteEngageMessageNumber = (accountId: number, phoneNumberId: number) =>
    Axios.delete(`/accounts/${accountId}/message_numbers/${phoneNumberId}`).then(
        response => response.data,
    );

const createEngageDomain = (
    accountId: number,
    { domain, is_subdomain, reply_to }: { domain: string; is_subdomain: boolean; reply_to: string },
) =>
    Axios.post(`/accounts/${accountId}/message_domains`, {
        domain,
        is_subdomain,
        reply_to,
    }).then(response => response);

const deleteEngageDomain = (accountId: number, domainId: number) =>
    Axios.delete(`/accounts/${accountId}/message_domains/${domainId}`).then(response => response);

const editEngageDomain = (
    accountId: number,
    domainId: number,
    { reply_to }: { reply_to: string },
) =>
    Axios.patch(`/accounts/${accountId}/message_domains/${domainId}`, { reply_to }).then(
        response => response,
    );

const getEngageDomains = (accountId: number) =>
    Axios.get<PaginatedData<EngageDomain>>(`/accounts/${accountId}/message_domains`).then(
        response => response,
    );

const verifyEngageDomainDNS = (accountId: number, domainId: number) =>
    Axios.post(`/accounts/${accountId}/message_domains/${domainId}/verify`).then(
        response => response,
    );

const getEngageSenders = (accountId: number) =>
    Axios.get<PaginatedData<EngageSender>>(`/accounts/${accountId}/message_senders`).then(
        response => response,
    );

const createEngageSender = (
    accountId: number,
    {
        username,
        display_name,
        message_domain_id,
    }: { username: string; display_name: string; message_domain_id: number },
) =>
    Axios.post(`/accounts/${accountId}/message_senders`, {
        username,
        display_name,
        message_domain_id,
    }).then(response => response);

const deleteEngageSender = (accountId: number, messageSenderId: number) =>
    Axios.delete(`/accounts/${accountId}/message_senders/${messageSenderId}`).then(
        response => response,
    );

const editEngageSender = (
    accountId: number,
    messageSenderId: number,
    { display_name }: { display_name: string },
) =>
    Axios.patch(`/accounts/${accountId}/message_senders/${messageSenderId}`, {
        display_name,
    }).then(response => response);

const resendSenderVerificationEmail = (accountId: number, messageSenderId: number) =>
    Axios.post(`/accounts/${accountId}/message_senders/${messageSenderId}/verify`).then(
        response => response,
    );

const getEngageMessageTemplates = (params: FetchMessageTemplateParams) =>
    Axios.get<PaginatedData<EngageMessageTemplate>>(`/message_templates`, { params }).then(
        response => response.data,
    );

const getAccountEngageMessageTemplates = (
    accountId: number,
    channel: CreateEngageMessageParams['channel'] | CreateEngageMessageParams['channel'][],
) =>
    Axios.get<PaginatedData<EngageMessageTemplate>>(`/accounts/${accountId}/message_templates`, {
        params: { channel },
    }).then(response => response.data);

const createAccountMessageTemplate = (
    accountId: number,
    params: CreateAccountMessageTemplateParams,
) => Axios.post(`/accounts/${accountId}/message_templates`, params).then(response => response.data);

const deleteAccountMessageTemplate = (accountId: number, templateId: number) =>
    Axios.delete(`/accounts/${accountId}/message_templates/${templateId}`).then(
        response => response.data,
    );

const concatStatusStrings = (statuses: string[] = []) => statuses.join(',');

const getMessageNumbers = (accountId: number) =>
    Axios.get<PaginatedData<MessageNumber>>(`/accounts/${accountId}/message_numbers`).then(
        response => response.data.data,
    );

const getSmsKeywords = (accountId: number) =>
    Axios.get<PaginatedData<SmsKeyword>>(
        `/accounts/${accountId}/sms_keywords?type=message_numbers`,
    ).then(response => response.data.data);

const createSmsKeyword = (accountId: number, params: SmsKeywordParams) =>
    Axios.post<SmsKeyword>(`/accounts/${accountId}/sms_keywords`, params).then(
        response => response.data,
    );

const updateSmsKeyword = (accountId: number, keywordId: number, params: SmsKeywordParams) =>
    Axios.patch<SmsKeyword>(`/accounts/${accountId}/sms_keywords/${keywordId}`, params).then(
        response => response.data,
    );

const deleteSmsKeyword = (accountId: number, smsKeywordId?: number) =>
    Axios.delete(`/accounts/${accountId}/sms_keywords/${smsKeywordId}`);

const getMessageStats = (
    accountId: number,
    messageId: number,
    params?: {
        limit?: number;
        page?: number;
        filterBy?: string;
    },
) =>
    Axios.get<PaginatedData<EngageDelivery>>(
        `/accounts/${accountId}/messages/${messageId}/deliveries`,
        {
            params,
        },
    ).then(response => response.data);

const activateEngage = (accountId: number, status: EngageStatus) => {
    return Axios.patch(`/accounts/${accountId}/engage-configuration`, {
        status,
    });
};

const refreshStats = (accountId: number, messageId: number) =>
    Axios.post(`/accounts/${accountId}/messages/${messageId}/refresh-statistics`);

const getMergeFields = (accountId: number, params?: MergeFieldsParams) =>
    Axios.get<
        PaginatedData<MergeField> & {
            defaults: DefaultMergeField[];
            prefix: string;
            suffix: string;
        }
    >(`/accounts/${accountId}/merge_fields`, {
        params,
    }).then(({ data }) => data);

const createMergeTag = (
    accountId: number,
    data: Pick<MergeField, 'name' | 'value'> & { custom_field_id?: number },
) => Axios.post(`/accounts/${accountId}/merge_fields`, data);

const updateMergeTag = (accountId: number, data: Omit<MergeField, 'title'>) =>
    Axios.patch(`/accounts/${accountId}/merge_fields/${data.id}`, data);

const deleteMergeTag = (accountId: number, itemId: number) =>
    Axios.delete(`/accounts/${accountId}/merge_fields/${itemId}`);

const createEngageMessageStripeSession = (accountId: number, messageId: number) =>
    Axios.post<StripeCheckoutDetails>(`/accounts/${accountId}/messages/${messageId}/checkout`).then(
        data => data.data,
    );

const createMailingsOrder = (accountId: number, messageId: number, data: { session_id: string }) =>
    Axios.post(`/accounts/${accountId}/messages/${messageId}/checkout/callback`, data).then(
        data => data.data,
    );

const fetchTrackableLetters = (accountId: number, params: FetchEngageMessagesParams = {}) => {
    const { channel, status, friendlyStatus, notStatus, ...rest } = params;

    return Axios.get<EngageMessages>(`/accounts/${accountId}/messages/trackable`, {
        params: {
            ...rest,
            status: pickOrUndefined(!!status, concatStatusStrings(status)),
            friendlyStatus: pickOrUndefined(!!friendlyStatus, concatStatusStrings(friendlyStatus)),
            notStatus: pickOrUndefined(!!notStatus, concatStatusStrings(notStatus)),
            channel: pickOrUndefined(!!channel, concatStatusStrings(channel)),
        },
    }).then(response => response.data);
};

const fetchLetterTracking = (accountId: number, messageId: number, params: FilterParams) => {
    const { filters, ...restParams } = params;
    const stringifiedFilters = filters ? qs.stringify(filters) : '';

    return Axios.get<PaginatedData<LetterFulfillment>>(
        `/accounts/${accountId}/messages/${messageId}/mail-pieces?${stringifiedFilters}`,
        { params: restParams },
    ).then(response => response.data);
};

const fetchLetterMetrics = (accountId: number, messageId: number) =>
    Axios.get<{
        metrics: { qr_code_scans: number; revenue_generated: string; total_donations: number };
    }>(`/accounts/${accountId}/messages/${messageId}/metrics`).then(response => response.data);

const fetchLetterFulfillmentStats = (accountId: number, messageId: number) =>
    Axios.get<FetchLetterFulfillmentStatsResponse>(
        `/accounts/${accountId}/messages/${messageId}/mail-pieces/stats`,
    ).then(response => response.data);

const fetchMessageCheckoutDetails = (accountId: number, messageId: number) =>
    Axios.get<FetchMessageCheckoutDetailsResponse>(
        `/accounts/${accountId}/messages/${messageId}/checkout/details`,
    ).then(response => response.data);

const fetchLetterProofs = (accountId: number, messageId: number) =>
    Axios.get<PaginatedData<LetterFulfillment>>(
        `/accounts/${accountId}/messages/${messageId}/mail-proofs`,
    ).then(response => response.data);

const createLetterProofStripeSession = (
    accountId: number,
    messageId: number,
    data: CreateLetterProofStripeSessionParams,
) =>
    Axios.post<LetterProofStripeSessionResponse>(
        `/accounts/${accountId}/messages/${messageId}/mail-proofs`,
        data,
    ).then(data => data.data);

const sendLetterProof = (accountId: number, messageId: number, mailPieceId: number) =>
    Axios.post(`/accounts/${accountId}/messages/${messageId}/mail-proofs/${mailPieceId}/send`).then(
        data => data.data,
    );

const fetchLetterProofCheckoutDetails = (
    accountId: number,
    messageId: number,
    mailPieceId: number,
) =>
    Axios.get<{
        checkout_details: null | {
            session_id: string;
            order_number: string | null;
            invoice_url: string | null;
            quantity: number;
            unit_price: string;
            total_amount: string;
            payment_method: {
                id: string;
                cc_last_four: string;
                method_subtype: TransactionSubtype;
                exp_month: number;
                exp_year: number;
                cc_expire: string;
            };
        };
        mail_piece: LetterFulfillment;
    }>(
        `/accounts/${accountId}/messages/${messageId}/mail-proofs/${mailPieceId}/checkout-details`,
    ).then(response => response.data);

const sendTestMessage = (accountId: number, messageId: number, params: { addresses: string[] }) =>
    Axios.post(`/accounts/${accountId}/messages/${messageId}/send-test-messages`, params).then(
        response => response.data,
    );

const fetchAdvancedEmailTemplates = (params?: FetchAdvancedEmailTemplateParams) =>
    Axios.get<AdvancedMessageTemplatesResponse>(`advanced-email/templates`, {
        params: {
            template_type: 'email',
            limit: 20,
            ...params,
        },
    }).then(response => response.data);

const fetchAdvancedEmailTemplateById = (id: string) =>
    Axios.get<AdvancedMessageTemplateDetail>(`advanced-email/templates/${id}`).then(
        response => response.data,
    );

const fetchAdvancedEmailToken = (accountId: number) =>
    Axios.get<BeeFreeAuthToken | null>(`/accounts/${accountId}/advanced-email/auth`).then(
        response => response.data,
    );

const createOrUpdatePersonalizedLetter = (
    accountId: number,
    messageId: number,
    contactId: number,
    data: { body: string },
) =>
    Axios.post<LetterFulfillment>(
        `/accounts/${accountId}/messages/${messageId}/mail-pieces/custom`,
        {
            contact_id: contactId,
            body: data.body,
        },
    ).then(response => response.data);

const revertPersonalizedLetter = (accountId: number, messageId: number, contactId: number) =>
    Axios.delete(`/accounts/${accountId}/messages/${messageId}/mail-pieces/custom`, {
        data: {
            contact_id: contactId,
        },
    }).then(response => response.data);

const removeContactFromRecipientsList = (accountId: number, messageId: number, contactId: number) =>
    Axios.post<LetterFulfillment>(
        `/accounts/${accountId}/messages/${messageId}/mail-pieces/remove`,
        {
            contact_id: contactId,
        },
    ).then(response => response.data);

const undoRemoveContactFromRecipientsList = (
    accountId: number,
    messageId: number,
    contactId: number,
) =>
    Axios.post<LetterFulfillment>(
        `/accounts/${accountId}/messages/${messageId}/mail-pieces/undo-remove`,
        {
            contact_id: contactId,
        },
    ).then(response => response.data);

const removeAllPersonalizedLetters = (accountId: number, messageId: number) =>
    Axios.delete(`/accounts/${accountId}/messages/${messageId}/mail-pieces/all`).then(
        response => response.data,
    );

const fetchEngageMessagePreviewStatus = (accountId: number, messageId: number) =>
    Axios.get<EngageMessagePreviewStatusResponse>(
        `/accounts/${accountId}/messages/${messageId}/preview-status`,
    ).then(response => response.data);

const fetchEngageMailPiecePreviewStatus = (
    accountId: number,
    messageId: number,
    contactId: number,
) =>
    Axios.get<EngageMessagePreviewStatusResponse>(
        `/accounts/${accountId}/messages/${messageId}/contacts/${contactId}/preview-status`,
    ).then(response => response.data);

const fetchMailPieceById = (accountId: number, messageId: number, mailpieceId: number) =>
    Axios.get<LetterFulfillment>(
        `/accounts/${accountId}/messages/${messageId}/mail-pieces/${mailpieceId}`,
        {
            params: {
                include: 'transactions',
            },
        },
    ).then(response => response.data);

const retryMailPieceById = (accountId: number, messageId: number, mailpieceId: number) =>
    Axios.post<LetterFulfillment>(
        `/accounts/${accountId}/messages/${messageId}/mail-pieces/${mailpieceId}/retry`,
    ).then(response => response.data);

const fetchMessageDelivery = (accountId: number, messageId: number, deliveryId: number) =>
    Axios.get<EngageDelivery>(
        `/accounts/${accountId}/messages/${messageId}/deliveries/${deliveryId}`,
    ).then(response => response.data);

export {
    getEngageConfiguration,
    getAvailableNumbers,
    postPhoneNumber,
    provisionEngageRegistration,
    createEngageMessage,
    uploadEngageMessageMedia,
    deleteEngageMessageMedia,
    updateEngageMessageNumber,
    deleteEngageMessageNumber,
    createEngageDomain,
    deleteEngageDomain,
    editEngageDomain,
    getEngageDomains,
    verifyEngageDomainDNS,
    getEngageSenders,
    createEngageSender,
    resendSenderVerificationEmail,
    deleteEngageSender,
    editEngageSender,
    getEngageMessageTemplates,
    getAccountEngageMessageTemplates,
    createAccountMessageTemplate,
    deleteAccountMessageTemplate,
    fetchEngageMessages,
    fetchEngageMessageById,
    updateEngageMessageById,
    duplicateEngageMessage,
    deleteEngageMessage,
    generateEngageLetterZip,
    getMessageNumbers,
    getSmsKeywords,
    createSmsKeyword,
    updateSmsKeyword,
    deleteSmsKeyword,
    getMessageStats,
    activateEngage,
    refreshStats,
    unscheduleEngageMessage,
    getMergeFields,
    createMergeTag,
    updateMergeTag,
    deleteMergeTag,
    createEngageMessageStripeSession,
    createLetterProofStripeSession,
    createMailingsOrder,
    sendLetterProof,
    fetchLetterTracking,
    fetchLetterFulfillmentStats,
    fetchLetterProofs,
    sendTestMessage,
    fetchAdvancedEmailTemplates,
    fetchAdvancedEmailTemplateById,
    fetchAdvancedEmailToken,
    fetchMessageCheckoutDetails,
    fetchLetterProofCheckoutDetails,
    fetchLetterMetrics,
    createOrUpdatePersonalizedLetter,
    revertPersonalizedLetter,
    removeAllPersonalizedLetters,
    removeContactFromRecipientsList,
    undoRemoveContactFromRecipientsList,
    fetchEngageMessagePreviewStatus,
    fetchEngageMailPiecePreviewStatus,
    fetchMailPieceById,
    retryMailPieceById,
    fetchMessageDelivery,
    fetchTrackableLetters,
};

export type {
    CreateEngageMessageParams,
    FetchEngageMessagesParams,
    UpdateEngageMessageNumberParams,
    EngageMessages,
    MergeFieldsParams,
    FetchAdvancedEmailTemplateParams,
    CreateLetterProofStripeSessionParams,
    LetterProofStripeSessionResponse,
    EngageMessagePreviewStatusResponse,
    FetchMessageTemplateParams,
    SmsKeywordParams,
};
